import { useMemo } from 'react';
import { useSecurity } from '../auth/Security';
import type { QuickAddButtonProps } from './types';

/**
 * Resolves ListView quick-add toolbar props through the centralized ABAC framework.
 *
 * Callers pass caller intent (for example `enableQuickAddButtons`); this hook returns
 * secured values that combine intent with `useSecurity` decisions.
 */
export function useListViewToolbarQuickAddButtonPropsSecurity(
    quickAddButtonProps: QuickAddButtonProps | undefined
): QuickAddButtonProps | undefined {
    if (!quickAddButtonProps) {
        return undefined;
    }

    const { enableQuickAddButtons, securityAction, securityFeature } = quickAddButtonProps;

    const { allowed: canPerformQuickAdd } = useSecurity(securityAction, { featureName: securityFeature });

    return useMemo((): QuickAddButtonProps | undefined => {
        if (securityAction === undefined) {
            return quickAddButtonProps;
        }

        return {
            ...quickAddButtonProps,
            enableQuickAddButtons: Boolean(enableQuickAddButtons) && canPerformQuickAdd
        };
    }, [quickAddButtonProps, enableQuickAddButtons, securityAction, canPerformQuickAdd]);
}
